import type { RoomPlayerReport } from '../store/roomStore'
import type { WebinarRoomPlayerState } from '../types'

/**
 * What a player is doing, read off the `<mux-player>` element. Mux Player's own controls are hidden (they would sit
 * over the client's title band), so the room draws its own from this.
 */

/** `idle`: no player is mounted. `loading`: mounted, `<mux-player>` not defined yet. `failed`: its code never loaded. */
export type PlayerStatus = 'idle' | 'loading' | 'ready' | 'failed'

export interface PlayerMedia {
    status: PlayerStatus
    paused: boolean
    ended: boolean
    /** Wants to play but has too little media to: the spinner case, and what the heartbeat calls Buffering. */
    buffering: boolean
    muted: boolean
    fullscreen: boolean
}

export const IDLE_MEDIA: PlayerMedia = {
    status: 'idle',
    paused: true,
    ended: false,
    buffering: false,
    muted: false,
    fullscreen: false,
}

/** `HTMLMediaElement.HAVE_FUTURE_DATA`: below it, playing stalls. */
const HAVE_FUTURE_DATA = 3

export interface MediaReading {
    paused?: boolean
    ended?: boolean
    readyState?: number
    muted?: boolean
}

/**
 * The element's state, from its properties rather than from which event fired last, so a missed or reordered event
 * cannot leave it wrong. Before `<mux-player>` is defined the element has none of these and reads as paused.
 */
export function readMedia(element: MediaReading): Pick<PlayerMedia, 'paused' | 'ended' | 'buffering' | 'muted'> {
    const paused = element.paused ?? true
    const ended = element.ended ?? false
    return {
        paused,
        ended,
        buffering: !paused && !ended && (element.readyState ?? 0) < HAVE_FUTURE_DATA,
        muted: element.muted ?? false,
    }
}

/** What the heartbeat reports the player doing (`WebinarRoomStates.PlayerState`). */
export function playerStateOf(media: PlayerMedia): WebinarRoomPlayerState {
    if (media.status === 'idle') return 'Slate'
    if (media.status === 'loading') return 'Buffering'
    if (media.status === 'failed' || media.paused || media.ended) return 'Paused'
    return media.buffering ? 'Buffering' : 'Playing'
}

const minuteOf = (seconds: number | null) => (seconds === null ? null : Math.floor(seconds / 60))

/**
 * Whether a new reading is worth telling the store. The heartbeat sends the last one every 30 s and the API counts
 * replay minutes by the minute a position falls in, so a position matters only when its minute changes: reporting
 * every `timeupdate` would re-render the whole page four times a second for nothing.
 */
export function shouldReport(previous: RoomPlayerReport, next: RoomPlayerReport): boolean {
    return previous.state !== next.state || minuteOf(previous.positionSeconds) !== minuteOf(next.positionSeconds)
}
